<template>
  <div class="text--center hidden-md-up pt-md pb-md">
    <sib-button
      class="product-button-write-review"
      size="md"
      @click="isCollapsed = !isCollapsed"
    >
      Write your review
    </sib-button>
  </div>
  <Collapsible
    :isCollapsed="isCollapsed"
    :max-height="0"
    :min-height-to-collapse="0"
  >
    <template #body>
      <div class="product-submit-review-form">
        <form @submit="onSubmit" ref="form">
          <input type="hidden" name="id" :value="rawProductId" />
          <h3 class="text--center pb-md">Write Comment</h3>
          <div
            class="review-form-item review-form-item--inline"
            :class="{ [FORM_ITEM_INVALID_CLASS]: !isRateValid }"
          >
            <label>RATING:</label>
            <div class="star-box" @mouseout="rateOnHover = 0">
              <span
                class="jdgm-star"
                :class="getStarClasses(index)"
                @click="selectRate(index)"
                @mouseover="rateOnHover = index"
                v-for="index of 5"
                :key="index"
              ></span>
            </div>
            <span class="review-form-item__message" v-show="!isRateValid">
              This field is required
            </span>
            <input type="hidden" :value="formData.rate" name="rating" />
          </div>

          <sib-form-item
            label="Name"
            input-id="review-form__name"
            :value="formData.name"
            :is-required="true"
            ref="itemName"
          >
            <sib-input
              id="review-form__name"
              size="md"
              placeholder="Enter your name (public)"
              v-model="formData.name"
              name="name"
            ></sib-input>
          </sib-form-item>

          <sib-form-item
            label="Email"
            input-id="review-form__email"
            :value="formData.email"
            :is-required="true"
            ref="itemEmail"
            :validator="isEmail"
            error-message="Email is invalid"
          >
            <sib-input
              id="review-form__email"
              size="md"
              placeholder="Enter your email (private)"
              v-model="formData.email"
              name="email"
            ></sib-input>
          </sib-form-item>

          <sib-form-item
            label="Review Title"
            input-id="review-form__title"
            :value="formData.title"
            ref="itemTitle"
          >
            <sib-input
              id="review-form__title"
              size="md"
              placeholder="Give your review a title"
              v-model="formData.title"
              name="title"
            ></sib-input>
          </sib-form-item>

          <sib-form-item
            label="Review"
            input-id="review-form__content"
            :value="formData.content"
            :is-required="true"
            ref="itemContent"
          >
            <sib-input
              id="review-form__content"
              size="md"
              placeholder="Write your comments here"
              rows="10"
              v-model="formData.content"
              type="textarea"
              name="body"
            ></sib-input>
          </sib-form-item>

          <sib-form-item
            v-for="question of productStore.judgemeReviewCustomQuestions"
            :key="question.id"
            :label="question.question"
          >
            <input
              type="hidden"
              :name="`cf_answers[${question.id}][cf_question_id]`"
              :value="question.id"
            />
            <Radio
              :options="transformOptions(question.cf_options)"
              :name="`cf_answers[${question.id}][value]`"
            ></Radio>
          </sib-form-item>

          <div class="review-form-item review-form-item-picture">
            <div class="file-label__text">Picture/Video (optional)</div>
            <label for="review-form__picture">
              <div class="file-label__image">
                <img
                  :src="SelectImageIcon"
                  alt="select image"
                  width="96"
                  height="96"
                />
              </div>
            </label>
            <input
              type="file"
              ref="imageSelect"
              id="review-form__picture"
              accept="image/*"
              multiple
            />
            <div
              class="review-form-preview-image"
              v-for="(selectedImage, index) of selectedImages"
              :key="index"
            >
              <div
                class="review-form-preview-image__loading lds-ring"
                v-if="selectedImage.status === 'uploading'"
              >
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              <img
                class="review-form-preview-image"
                :src="selectedImage.base64"
                :alt="selectedImage.file.name"
              />
            </div>
          </div>
          <div class="product-review-form-button-wrapper">
            <sib-button
              role="primary"
              size="md"
              width="auto"
              :loading="loading"
            >
              Submit Review
            </sib-button>
            <div
              class="sib-button sib-button--default sib-button--md sib-button--width-default sib-button--round-md product-button-write-review"
              @click="isCollapsed = !isCollapsed"
            >
              Cancel Review
            </div>
          </div>

          <p class="review-form__success-message" v-show="submited">
            Thank you! Your review will be published as soon as it is approved
            by the shop admin. You can remove or edit your review by logging
            into
            <a class="link" href="https://judge.me/login" target="_blank">
              Judge.me
            </a>
          </p>
          <p class="review-form__error-message" v-show="isError">
            Something went wrong. Please try again later.
          </p>
          <p class="review-form__error-message pt-xs" v-show="isWarning">
            Thank you for your interest in our products! To leave a review, you
            need to have purchased the product. Please make sure your order has
            been processed and completed.
          </p>
        </form>
      </div>
    </template>
  </Collapsible>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, reactive, ref, watch } from 'vue'
import SelectImageIcon from '@/assets/images/icons/icon-image-select.png'
import Provider from '@/provider'
import FormItem from '@/ui-kits/FormItem.vue'
import {
  FORM_ITEM_INVALID_SELECTOR,
  HEADER_HEIGHT,
  isEmail,
  FORM_ITEM_INVALID_CLASS,
  EVENT_CLICK_PRODUCT_FORM_REVIEW_BUTTON,
} from '@/utils'
import { computed } from '@vue/reactivity'
import useProductStore from '@/store/product/index'
import { getRawIdFromShopifyId } from '@/utils/product'
import { KeyValue } from '@/types'
import Analytics from '@/services/analytics'
import Logger from '@/services/log'
import Radio from '@/ui-kits/Radio.vue'
import SibFormItem from '@/ui-kits/FormItem.vue'
import Collapsible from '@/ui-kits/Collapsible.vue'
import EventBus from '@/services/eventbus'

interface SelectedImages {
  file: File
  url: string
  base64: string
  status: 'added' | 'uploading' | 'uploaded'
}

const productStore = useProductStore()

const formData = reactive({
  name: '',
  email: '',
  title: '',
  content: '',
  rate: 0,
})

const imageSelect = ref<HTMLInputElement | null>(null)
const selectedImages: SelectedImages[] = reactive([])
const form = ref<HTMLFormElement | null>(null)
const itemName = ref<typeof FormItem | null>(null)
const itemEmail = ref<typeof FormItem | null>(null)
const itemContent = ref<typeof FormItem | null>(null)
const rateOnHover = ref(0)
const shouldValidate = ref(false)
const loading = ref(false)
const submited = ref(false)
const isError = ref(false)
const isWarning = ref(false)
const isCollapsed = ref(true)
const isRateValid = computed(() => {
  return !shouldValidate.value || formData.rate > 0
})

const rawProductId = computed(() =>
  getRawIdFromShopifyId(productStore.product?.id)
)

onMounted(() => {
  // because this component mouted before Product component
  // so we must wait fetch product to get rawProductId then call api to fetch custom question
  watch(
    () => rawProductId.value,
    (value) => {
      // if (value) fetchCustomQuestions()
    },
    { immediate: true }
  )
  bindImageUploadInput()
  EventBus.on(EVENT_CLICK_PRODUCT_FORM_REVIEW_BUTTON, () => {
    isCollapsed.value = !isCollapsed.value
  })
})

function bindImageUploadInput() {
  if (!imageSelect.value) return

  imageSelect.value.addEventListener('change', async () => {
    if (!imageSelect.value) return
    if (!imageSelect.value.files?.length) return
    const provider = await Provider.getInstance()

    for (const file of imageSelect.value.files) {
      const existsIndex = selectedImages.findIndex((elm) => {
        return elm.file.name == file.name && elm.file.size == file.size
      })
      // Do not add duplicate image
      if (existsIndex > -1) continue
      // Add the image to the selected array
      selectedImages.push({
        file,
        url: '',
        base64: URL.createObjectURL(file),
        status: 'added',
      })
    }
    // upload image to s3
    selectedImages.forEach(async (selectedImage) => {
      if (selectedImage.status !== 'added') return
      selectedImage.status = 'uploading'
      const url = await provider.jugdemeUploadImage(selectedImage.file)
      selectedImage.url = url
      selectedImage.status = 'uploaded'
    })
  })
}

async function fetchCustomQuestions() {
  try {
    if (productStore.judgemeReviewCustomQuestions.length) return
    const provider = await Provider.getInstance()
    const response = await provider.judgemeGetCustomQuestion({
      productId: rawProductId.value,
    })
    productStore.judgemeReviewCustomQuestions = response.cf_questions
  } catch (error: any) {
    Logger.error('Error when fetch custom question', error)
    Analytics.error(error)
  }
}

function getStarClasses(index: number) {
  let rating = rateOnHover.value > 0 ? rateOnHover.value : formData.rate
  return rating >= index ? 'jdgm--on' : 'jdgm--off'
}

async function getCustomerOrderNumber(email: string, firstName: string) {
  try {
    const provider = await Provider.getInstance()
    const respone = await provider.getOrderNumberByEmail(email, firstName)
    if (Number.isInteger(respone)) return respone
    return 0
  } catch (error: any) {
    return 0
  }
}

async function onSubmit(event: Event) {
  if (!productStore.product?.id) return
  shouldValidate.value = true
  event.preventDefault()
  const isNameValid = itemName.value?.validate()
  const isEmailValid = itemEmail.value?.validate()
  const isContentValid = itemContent.value?.validate()
  const isValid =
    isNameValid && isEmailValid && isContentValid && isRateValid.value
  if (!isValid) {
    // wait a tick for Vue applying the classes
    await nextTick()
    const firstInvalidElement: HTMLElement | undefined | null =
      form.value?.querySelector(FORM_ITEM_INVALID_SELECTOR)
    if (firstInvalidElement) {
      window.scrollTo({
        top:
          window.scrollY +
          firstInvalidElement.getBoundingClientRect().top -
          HEADER_HEIGHT,
        behavior: 'smooth',
      })
    }
    return
  }

  if (!form.value) return

  loading.value = true
  submited.value = false
  isError.value = false
  isWarning.value = false

  const customerNumber = await getCustomerOrderNumber(
    formData.email,
    formData.name
  )
  if (!customerNumber) {
    loading.value = false
    isWarning.value = true
    return
  }
  // submit review to jugdeme
  try {
    const submitingFormData = new FormData(form.value)
    selectedImages
      .filter((elm) => elm.status == 'uploaded')
      .forEach((element) => {
        submitingFormData.append('picture_urls[]', element.url)
      })
    const provider = await Provider.getInstance()
    await provider.judgemeSubmitReview(submitingFormData)
    submited.value = true
  } catch (error: any) {
    Logger.error('Error when submiting review', error)
    Analytics.error(error)
    isError.value = true
  }

  loading.value = false
}

function selectRate(input: number) {
  formData.rate = input
}

function transformOptions(
  options: { id: number; title: string }[]
): KeyValue[] {
  if (!options) return []
  return options.map(({ title }) => {
    return {
      key: title,
      value: title,
    }
  })
}
</script>

<style lang="scss">
.product-button-write-review {
  margin-bottom: 2em;
  width: auto;
}

.product-submit-review-form {
  margin-top: 2rem !important;
  padding: 1em;
  border: solid 1px var(--border-color);
  margin-bottom: 2em;
  --input-select-image-size: 96px;
  form {
    display: flex;
    flex-direction: column;
    .sib-button {
      align-self: center;
    }
    .sib-form-item__label {
      margin: 1.75em 0 0.75em 0 !important;
    }
  }
}
.product-review-form-button-wrapper {
  padding-top: 1.5rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.review-form-item {
  margin-bottom: 1.55em;
  position: relative;
  label {
    font-weight: 600;
    display: block;
    margin-bottom: 3px;
    cursor: pointer;
  }
  &--inline {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1em;
    label {
      width: unset !important;
      margin: unset !important;
    }
  }
  &-picture {
    label {
      display: inline-block;
    }
    input {
      display: none;
    }
  }
  &__message {
    position: absolute;
    top: 100%;
    font-size: 0.875em;
    color: var(--color-secondary);
  }
}

.star-box {
  display: flex;
  gap: 6px;
  .jdgm-star {
    cursor: pointer;
  }
}

.review-form-item-picture {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em 1em;
}

.file-label__text {
  margin-top: 1.5em !important;
  margin-bottom: 3px;
  width: 100%;
  font-weight: 600;
}

.file-label__image {
  border-radius: var(--border-radius-large);
  border: dashed 1px var(--border-color);
  display: inline-flex;
}

.review-form-preview-image {
  width: var(--input-select-image-size);
  height: var(--input-select-image-size);
  border: solid 1px var(--border-color);
  position: relative;
  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
    font-size: 10px;
    --loading-size: 20px;
    --loading-line-width: 2px;
    color: #888888;
  }
}
.review-form__success-message {
  color: var(--color-primary);
  .link {
    text-decoration: underline;
  }
}
.review-form__error-message {
  color: var(--color-secondary);
}
</style>
